import { ParameterConfig } from 'app/model/pipeline/parameter-config';
import { AzureSqlTableDataset } from 'app/model/dataset/azure-sql-table.dataset';
import { IModel, IAttribute, IViolation } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { createParameterConfigurationValidator } from 'app/model/adapters/linkedservices/parameter-config.util';
import { writeBehaviorOptionValues } from 'app/shared/constants/write-behavior.const';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { Model } from 'app/model/backendmodel';
import {
    ICopySinkWithUpdateMethod,
    ModelConstants,
    SqlServerTableDataset,
    TabularCopySink,
    UpdateMethod,
    UpdateMethodType
} from 'shell-models-lib';
import { validateUniqueKeyColumns } from './copysink.utils';
import { CopyJobActivity } from 'app/model/copyjob/copy-job.activity';

export type SqlTypeCopySinkMode = 'table' | 'storedProcedure';
// note: only 'upsert' & 'insert' is accepted by BE, 'storedProcedure' is only used by UI
export type WriteBehaviorTypes = 'upsert' | 'insert' | 'storedProcedure' | 'SCD2';

@Metamodel.model({
    name: 'SqlSink',
    display: [],
    referable: true,
    tags: { datasetModel: [Metamodel.modelFor(AzureSqlTableDataset), Metamodel.modelFor(SqlServerTableDataset)] }
})
export class SqlSink extends TabularCopySink implements ICopySinkWithUpdateMethod {
    public static readonly MODEL: IModel;
    public static readonly WRITER_STORED_PROCEDURE_NAME: IAttribute;
    public static readonly WRITER_STORED_PROCEDURE_PARAMETERS: IAttribute;
    public static readonly WRITER_TABLE_TYPE: IAttribute;
    public static readonly WRITER_TABLE_TYPE_PARAMETER_NAME: IAttribute;
    public static readonly TABLE_OPTION: IAttribute;
    public static readonly SQL_WRITER_USE_TABLE_LOCK: IAttribute;
    public static readonly DISABLE_METRICS_COLLECTION: IAttribute;
    public static readonly WRITE_BEHAVIOR: IAttribute;
    public static readonly WRITE_BEHAVIOR_USE_TEMP_DB: IAttribute;
    public static readonly WRITE_BEHAVIOR_INTERIM_SCHEMA_NAME: IAttribute;
    public static readonly WRITE_BEHAVIOR_KEYS: IAttribute;
    public static readonly UPSERT_SETTINGS: IAttribute;

    @Metamodel.attribute({
        templatable: true,
        association: 'WRITER_STORED_PROCEDURE_NAME',
        display: stringResources.Dataset_WriterStoredProcedure
    })
    public sqlWriterStoredProcedureName?: string;

    @Metamodel.attribute({
        display: stringResources.Dataset_WriterStoredProcedure_SqlWriterParameter,
        constraint: {
            validator: createParameterConfigurationValidator(true)
        },
        accepts: ['Object', 'Array'],
        association: 'WRITER_STORED_PROCEDURE_PARAMETERS'
    })
    public storedProcedureParameters?: ParameterConfig[] = [];

    @Metamodel.attribute({
        templatable: true,
        association: 'WRITER_TABLE_TYPE',
        display: stringResources.Dataset_WriterStoredProcedure_SqlWriterType
    })
    public sqlWriterTableType?: string;

    @Metamodel.attribute({
        templatable: true,
        association: 'WRITER_TABLE_TYPE_PARAMETER_NAME',
        display: stringResources.Dataset_WriterStoredProcedure_TableTypeParameterName
    })
    public storedProcedureTableTypeParameterName?: string;

    @Metamodel.attribute({
        templatable: true,
        association: 'TABLE_OPTION',
        display: stringResources.Dataset_WriterTableOption
    })
    public tableOption?: string;

    @Metamodel.attribute({
        association: 'SQL_WRITER_USE_TABLE_LOCK',
        display: stringResources.Dataset_UseBulkInsertTableLock
    })
    public sqlWriterUseTableLock?: boolean = false;

    @Metamodel.attribute({
        templatable: true,
        association: 'DISABLE_METRICS_COLLECTION',
        display: stringResources.CopySinkDisableMetricsCollectionLabel
    })
    public disableMetricsCollection: boolean;

    @Metamodel.attribute({
        templatable: true,
        association: 'WRITE_BEHAVIOR',
        display: stringResources.ModelName_WriteBehavior
    })
    public writeBehavior?: WriteBehaviorTypes = 'insert';

    @Metamodel.attribute({
        templatable: true,
        association: 'WRITE_BEHAVIOR_USE_TEMP_DB',
        display: stringResources.SqlUseTmpDB
    })
    public writeBehaviorUseTempDB?: boolean = true;

    @Metamodel.attribute({
        templatable: true,
        association: 'WRITE_BEHAVIOR_INTERIM_SCHEMA_NAME',
        display: stringResources.SqlStagingSchemaLabel
    })
    public writeBehaviorInterimSchemaName?: string;

    @Metamodel.attribute({
        templatable: true,
        association: 'WRITE_BEHAVIOR_KEYS',
        display: stringResources.DataFlowDatasetKeyColumns,
        accepts: ['Array', 'Object'],
        constraint: {
            validator: SqlSink.validateKeyColumnsMappingForModelValidator
        }
    })
    public writeBehaviorKeys?: string[];

    // for backward compatiblity where metadata-driven template grouped all upsertSettings in 1
    @Metamodel.attribute({
        templatable: true,
        association: 'UPSERT_SETTINGS'
    })
    public upsertSettings?: Model.WriteBehaviorUpsertOptions;

    // UI model only
    public get mode(): SqlTypeCopySinkMode {
        return this.sqlWriterStoredProcedureName != null || SqlSink.WRITER_STORED_PROCEDURE_NAME.templateValue(this)
            ? 'storedProcedure'
            : 'table';
    }

    public get updateMethod(): UpdateMethodType {
        switch (this.writeBehavior) {
            case 'insert':
                return UpdateMethod.Append;
            case 'upsert':
                return UpdateMethod.Upsert;
            case 'SCD2':
                return UpdateMethod.SCD2;
            default:
                return undefined;
        }
    }

    public set updateMethod(value: UpdateMethodType) {
        switch (value) {
            case UpdateMethod.Append:
                this.writeBehavior = 'insert';
                break;
            case UpdateMethod.Upsert:
                this.writeBehavior = 'upsert';
                break;
            case UpdateMethod.SCD2:
                this.writeBehavior = 'SCD2';
                break;
            default:
        }
    }

    public get upsertKeyColumns(): string[] {
        return this.writeBehaviorKeys;
    }

    public set upsertKeyColumns(value: string[]) {
        this.writeBehaviorKeys = value;
    }

    public static validateKeyColumnsMappingForModelValidator(sink: SqlSink): IViolation[] {
        return SqlSink.validateKeyColumnsMapping(sink);
    }

    public static validateKeyColumnsMapping(sink: SqlSink, updateMethodLabel?: string): IViolation[] {
        if (updateMethodLabel == null) {
            if (sink.owner != null && Metamodel.modelOf(sink.owner).name === ModelConstants.copyJobActivity) {
                updateMethodLabel =
                    (sink.owner as CopyJobActivity).updateMethod === UpdateMethod.SCD2
                        ? stringResources.WriteBehavior_Option_SCD2
                        : stringResources.MergeLabel;
            } else {
                updateMethodLabel = stringResources.WriteBehavior_Option_Upsert;
            }
        }
        const writeBehavior = ValueAdapter.toResource(sink, SqlSink.WRITE_BEHAVIOR);
        if (
            sink?.owner &&
            writeBehavior === writeBehaviorOptionValues.SCD2 &&
            Metamodel.modelOf(sink.owner).name === ModelConstants.copyJobActivity
        ) {
            return validateUniqueKeyColumns(
                sink,
                SqlSink.UPSERT_SETTINGS,
                sink.writeBehaviorKeys,
                true,
                updateMethodLabel
            );
        }
        if (writeBehavior !== writeBehaviorOptionValues.upsert) {
            // only check for key columns mapping when certain write behavior is upsert
            return [];
        }
        return validateUniqueKeyColumns(sink, SqlSink.UPSERT_SETTINGS, sink.writeBehaviorKeys, true, updateMethodLabel);
    }
}
